import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, NavLink, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import './wikihousestyle.css';
import './WikiLayout.css';
import logo from '../Media/logo.png';
import WikiHome from './WikiHome';
import WikiItemDetail from './WikiItemDetail';
import WikiItemList from './WikiItemList';
import WikiMobList from './WikiMobList';
import WikiMobDetail from './WikiMobDetail';
import WikiSetDetail from './WikiSetDetail';
import WikiSeasonInfo from './WikiSeasonInfo';
import WikiBlockList from './WikiBlockList';
import WikiUpdates from './WikiUpdates';
import WikiEvents from './WikiEvents';
import { PageWrapper, FadeIn } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';
import { getMobWikiUrl } from '../data/wiki_mobs_assets';
import api from '../API/Api';

function NavDropdown({ label, children, location, activePaths = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hasActive = activePaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (!ref.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="wiki-nav-dropdown" ref={ref}>
      <button
        className={`wiki-nav-dropdown-btn${open ? ' open' : ''}${hasActive ? ' has-active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        {label}
        <span className="wiki-nav-dropdown-caret">▼</span>
      </button>
      {open && (
        <div className="wiki-nav-dropdown-menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

function WikiNav({ allItems, allMobs }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('site_theme');
    return savedTheme === 'bright' ? 'bright' : 'default';
  });
  const [searchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState(null);
  const searchQuery = searchParams.get('q') || '';
  const searchWrapRef = useRef(null);

  useEffect(() => {
    if (!searchResults) return;
    function handler(e) {
      if (!searchWrapRef.current?.contains(e.target)) setSearchResults(null);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchResults]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    localStorage.setItem('site_theme', themeMode);
  }, [themeMode]);

  function toggleThemeMode() {
    setThemeMode((prev) => (prev === 'bright' ? 'default' : 'bright'));
  }

  const handleSearch = (e) => {
    const val = e.target.value;
    let path = location.pathname;
    if (path.includes('/item/')) path = '/wiki/items';

    if (val && val.length >= 2) {
      const qLower = val.toLowerCase();
      const itemMatches = (allItems || [])
        .filter(i => (i.name || '').toLowerCase().includes(qLower) || (i.category || '').toLowerCase().includes(qLower))
        .slice(0, 5);
      const mobMatches = (allMobs || [])
        .filter(m => (m.name || '').toLowerCase().includes(qLower) || (m.type || '').toLowerCase().includes(qLower))
        .slice(0, 5);
      setSearchResults({ items: itemMatches, mobs: mobMatches });
    } else {
      setSearchResults(null);
    }

    if (val) {
      navigate(`${path}?q=${encodeURIComponent(val)}`, { replace: true });
    } else {
      navigate(path, { replace: true });
    }
  };

  const handleSelectResult = (type, item) => {
    if (type === 'item') navigate(`/wiki/item/${item.slug}`);
    else if (type === 'mob') navigate(`/wiki/mob/${item.slug}`);
    setSearchResults(null);
  };

  return (
    <>
      <FadeIn y={-20} duration={0.6} className="wiki-nav">
        <div className="wiki-nav-brand" onClick={() => navigate('/wiki')}>
          <img src={logo} alt="SimpleMC" className="wiki-nav-logo" />
          <span className="wiki-nav-badge">Wiki</span>
        </div>

        <div className="wiki-nav-search-wrap" ref={searchWrapRef} style={{ position: 'relative' }}>
          <span className="wiki-nav-search-icon"><McIcon materialKey="spyglass" size={16} /></span>
          <input
            className="wiki-nav-search"
            type="text"
            placeholder="Search items, mobs…"
            value={searchQuery}
            onChange={handleSearch}
          />
          {searchResults && (searchResults.items.length > 0 || searchResults.mobs.length > 0) && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
              background: 'rgba(0,0,0,0.95)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              zIndex: 1000, maxHeight: '300px', overflowY: 'auto',
            }}>
              {searchResults.items.length > 0 && (
                <div>
                  <div style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Items</div>
                  {searchResults.items.map(item => (
                    <div key={item.id} onClick={() => handleSelectResult('item', item)}
                      style={{ padding: '0.75rem 1rem', borderTop: '1px solid rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'background 200ms', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}>
                        <McIcon materialKey={item.material || item.type || 'barrier'} size={28} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text)' }}>{item.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.category}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {searchResults.mobs.length > 0 && (
                <div style={{ borderTop: searchResults.items.length > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mobs</div>
                  {searchResults.mobs.map(mob => {
                    const mobIconUrl = getMobWikiUrl(mob.mob_type || mob.type, mob.name);
                    return (
                      <div key={mob.id} onClick={() => handleSelectResult('mob', mob)}
                        style={{ padding: '0.75rem 1rem', borderTop: '1px solid rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'background 200ms', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}>
                          {mobIconUrl && mobIconUrl.startsWith('http') ? (
                            <img src={mobIconUrl} alt={mob.name} style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                          ) : mob.icon ? (
                            <span style={{ fontSize: '1.2rem' }}>{mob.icon}</span>
                          ) : (
                            <span style={{ fontSize: '1.2rem' }}>🐾</span>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text)' }}>{mob.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{mob.type || 'Mob'}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="wiki-nav-links">
          <NavLink to="/wiki" end className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Home
          </NavLink>
          <NavLink to="/wiki/items"  className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Items
          </NavLink>
          <NavLink to="/wiki/mobs"   className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Mobs
          </NavLink>
          <NavLink to="/wiki/blocks" className={({ isActive }) => `wiki-nav-link${isActive ? ' active' : ''}`}>
            Blocks
          </NavLink>

          {/* Season dropdown: Events, Season Info, Updates */}
          <NavDropdown
            label="Season"
            location={location}
            activePaths={['/wiki/events', '/wiki/season', '/wiki/updates']}
          >
            <NavLink to="/wiki/events"  className={({ isActive }) => `wiki-nav-dropdown-item${isActive ? ' active' : ''}`}>
              Events
            </NavLink>
            <NavLink to="/wiki/season"  className={({ isActive }) => `wiki-nav-dropdown-item${isActive ? ' active' : ''}`}>
              Season Info
            </NavLink>
            <div className="wiki-nav-dropdown-divider" />
            <NavLink to="/wiki/updates" className={({ isActive }) => `wiki-nav-dropdown-item${isActive ? ' active' : ''}`}>
              Updates
            </NavLink>
          </NavDropdown>
        </div>

        <Link to="/" className="wiki-nav-back">← Back to SimpleMC</Link>

        <button
          className={`wiki-theme-toggle${themeMode === 'bright' ? ' is-bright' : ''}`}
          onClick={toggleThemeMode}
          title={themeMode === 'bright' ? 'Switch to default mode' : 'Switch to bright mode'}
          aria-label={themeMode === 'bright' ? 'Switch to default mode' : 'Switch to bright mode'}
        >
          <span className="wiki-theme-icon" aria-hidden="true">{themeMode === 'bright' ? '☀' : '☾'}</span>
        </button>

        <button
          className={`wiki-nav-hamburger${menuOpen ? " open" : ""}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle Menu"
        >
          <span /><span /><span />
        </button>
      </FadeIn>

      <div className={`wiki-mobile-drawer${menuOpen ? " open" : ""}`}>
        <div className="wiki-mobile-search-wrap">
          <input
            className="wiki-nav-search"
            type="text"
            placeholder="Search items, mobs…"
            value={searchQuery}
            onChange={handleSearch}
            autoFocus
          />
        </div>

        <NavLink to="/wiki" end className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Home</NavLink>
        <NavLink to="/wiki/items"  className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Items</NavLink>
        <NavLink to="/wiki/mobs"   className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Mobs</NavLink>
        <NavLink to="/wiki/blocks" className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Blocks</NavLink>

        <div className="wiki-mobile-divider" />

        <div style={{ padding: '0.25rem 1rem 0.1rem', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: 'var(--text-muted)' }}>Season</div>
        <NavLink to="/wiki/events"  className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Events</NavLink>
        <NavLink to="/wiki/season"  className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Season Info</NavLink>
        <NavLink to="/wiki/updates" className={({ isActive }) => `wiki-mobile-link${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>Updates</NavLink>

        <div className="wiki-mobile-divider" />
        <button className="wiki-theme-toggle-mobile" onClick={toggleThemeMode}>
          <span className="wiki-theme-icon" aria-hidden="true">{themeMode === 'bright' ? '☀' : '☾'}</span>
          <span>{themeMode === 'bright' ? 'Use Default Colors' : 'Use Bright Colors'}</span>
        </button>
        <Link to="/" className="wiki-mobile-link" onClick={() => setMenuOpen(false)}>← Back to SimpleMC</Link>
      </div>
    </>
  );
}

function WikiFooter() {
  return (
    <footer className="wiki-footer">
      <div className="wiki-footer-brand">Simple<em>MC</em> Wiki</div>
      <p>simplemcnetwork.net · Community Wiki · Season 1</p>
    </footer>
  );
}

export default function Wiki() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [mobs, setMobs] = useState([]);
  const [sets, setSets] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [itemsRes, mobsRes, setsRes, blocksRes] = await Promise.all([
          api('getwikiitems'),
          api('getwikimobs'),
          api('getwikisets'),
          api('getwikiblocks'),
        ]);

        if (itemsRes.isSuccess) setItems(itemsRes.data || []);
        if (mobsRes.isSuccess) setMobs(mobsRes.data || []);
        if (setsRes.isSuccess) setSets(setsRes.data || []);
        if (blocksRes.isSuccess) setBlocks(blocksRes.data || []);
      } catch (err) {
        console.error("Failed to load wiki data", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div className="wiki-root">
      <WikiNav allItems={items} allMobs={mobs} />
      <main>
        {loading ? (
          <div style={{ padding: '8rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading Wiki Database...
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route index element={<PageWrapper><WikiHome items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="items" element={<PageWrapper><WikiItemList items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="mobs" element={<PageWrapper><WikiMobList items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="blocks" element={<PageWrapper><WikiBlockList blocks={blocks} items={items} /></PageWrapper>} />
              <Route path="season" element={<PageWrapper><WikiSeasonInfo /></PageWrapper>} />
              <Route path="events" element={<PageWrapper><WikiEvents /></PageWrapper>} />
              <Route path="updates" element={<PageWrapper><WikiUpdates /></PageWrapper>} />
              <Route path="browse" element={<Navigate to="/wiki/mobs" replace />} />
              <Route path="item/:slug" element={<PageWrapper><WikiItemDetail items={items} mobs={mobs} sets={sets} /></PageWrapper>} />
              <Route path="mob/:slug" element={<PageWrapper><WikiMobDetail items={items} mobs={mobs} /></PageWrapper>} />
              <Route path="set/:slug" element={<PageWrapper><WikiSetDetail sets={sets} items={items} /></PageWrapper>} />
              <Route path="*" element={<PageWrapper><WikiHome items={items} mobs={mobs} /></PageWrapper>} />
            </Routes>
          </AnimatePresence>
        )}
      </main>
      <WikiFooter />
    </div>
  );
}
